import { useState } from "react";

import {
  listV2Pipelines,
  createV2Pipeline,
  deleteV2Pipeline,
  updateV2Pipeline,
  duplicateV2Pipeline,
  CreateV2PipelineRequest,
  UpdateV2PipelineRequest,
} from "./service";

export default usePipelinesV2;

interface LifecycleCbs {
  init: () => void;
  success: (data?: any) => void;
  error: (e: any) => void;
}

interface UsePipelinesV2 {
  pipelines: Array<any>;
  isLoading: boolean;
  listPipelines: (loading?:boolean) => void;
  createPipeline: (
    data: CreateV2PipelineRequest["body"],
    lifecycleCbs: LifecycleCbs,
  ) => void;
  deletePipeline: (pipelineId: string, lifecycleCbs: LifecycleCbs) => void;
  updatePipeline: (
    pipelineId: string,
    data: UpdateV2PipelineRequest["body"],
    lifecycleCbs: LifecycleCbs,
  ) => void;
  duplicatePipeline: (
    pipelineId: string,
    name: string,
    lifecycleCbs: LifecycleCbs,
  ) => void;
}

function usePipelinesV2(workspaceId: string): UsePipelinesV2 {
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  async function listPipelines(loading = true) {
    setIsLoading(loading);

    try {
      const request = { params: { workspaceId } };
      const response = await listV2Pipelines(request);

      setPipelines(response.data.pipelines);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  }

  async function createPipeline(
    data: CreateV2PipelineRequest["body"],
    lifecycleCbs: LifecycleCbs,
  ) {
    lifecycleCbs.init();

    try {
      const request = { params: { workspaceId }, body: data };
      const response = await createV2Pipeline(request);

      lifecycleCbs.success(response.data);
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  async function deletePipeline(
    pipelineId: string,
    lifecycleCbs: LifecycleCbs,
  ) {
    lifecycleCbs.init();

    try {
      const request = { params: { workspaceId, pipelineId } };
      const response = await deleteV2Pipeline(request);

      listPipelines(false);
      lifecycleCbs.success(response.data);
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  async function updatePipeline(
    pipelineId: string,
    data: UpdateV2PipelineRequest["body"],
    lifecycleCbs: LifecycleCbs,
  ) {
    lifecycleCbs.init();

    try {
      const request = { params: { workspaceId, pipelineId }, body: data };
      const response = await updateV2Pipeline(request);

      lifecycleCbs.success(response.data);
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  async function duplicatePipeline(
    pipelineId: string,
    name: string,
    lifecycleCbs: LifecycleCbs,
  ) {
    lifecycleCbs.init();

    try {
      const request = { params: { workspaceId, pipelineId }, body: { name } };
      const response = await duplicateV2Pipeline(request);

      listPipelines(false);
      lifecycleCbs.success(response.data);
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  return {
    pipelines,
    isLoading,
    listPipelines,
    deletePipeline,
    createPipeline,
    updatePipeline,
    duplicatePipeline,
  };
}
